import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { dt } from "@/lib/format";
import { Button, Card, DarkSelect, Input, Modal, PageHeader, Pill, Table, Td, Th } from "@/components/ui";
import { TelegramIcon } from "@/components/icons";
import { popup, confirmPopup } from "@/components/popup";

interface User {
  id: number;
  telegram_id: number;
  username: string | null;
  first_name: string | null;
  created_at: string;
  is_blocked: boolean;
  used_test: boolean;
}

interface RefSummary {
  id: number;
  telegram_id: number;
  username: string | null;
  first_name: string | null;
  created_at: string;
}

interface Access {
  id: number;
  user_id: number;
  server_id: number;
  secret: string;
  access_type: string;
  is_active: boolean;
  activated_at: string;
  expires_at: string;
  server?: { id: number; name: string; host: string } | null;
}

interface Payment {
  id: number;
  amount: number;
  currency: string;
  status: string;
  payment_method: string | null;
  payment_id: string | null;
  created_at: string;
}

interface ProxySub {
  id: number;
  plan: string;
  endpoint_id: number | null;
  endpoint_name: string | null;
  device_limit: number;
  is_active: boolean;
  expires_at: string | null;
  link: string;
}

interface Details {
  user: User;
  referral_percent: number | null;
  balance: number;
  referrer: RefSummary | null;
  referrals: RefSummary[];
  accesses: Access[];
  payments: Payment[];
  proxy_subs: ProxySub[];
}

interface VpnStatus {
  available: boolean;
  exists: boolean;
  active: boolean;
  status: string | null;
  expire_at: string | null;
  devices: number;
  device_limit: number;
  sub_url: string | null;
  short_uuid: string | null;
  uuid: string | null;
  panel_url: string | null;
}

interface Server {
  id: number;
  name: string;
  server_type: string;
  is_active: boolean;
}

const rub = new Intl.NumberFormat("ru-RU", {
  style: "currency",
  currency: "RUB",
  maximumFractionDigits: 0,
});

function formatPaymentAmount(amount: number, currency: string): string {
  if (currency === "XTR") {
    return `${amount.toLocaleString("ru-RU")} ⭐️`;
  }
  return rub.format(amount);
}


export default function UserDetails() {
  const { id } = useParams<{ id: string }>();
  const userId = Number(id);
  const qc = useQueryClient();

  const details = useQuery<Details>({
    queryKey: ["user-details", userId],
    queryFn: () => api<Details>(`/api/admin/users/${userId}/details`),
    enabled: !Number.isNaN(userId),
  });

  const servers = useQuery<{ servers: Server[] }>({
    queryKey: ["servers"],
    queryFn: () => api("/api/admin/servers"),
  });

  const vpn = useQuery<VpnStatus>({
    queryKey: ["user-vpn", userId],
    queryFn: () => api<VpnStatus>(`/api/admin/users/${userId}/vpn`),
    enabled: !Number.isNaN(userId),
  });

  const botInfo = useQuery<{ username: string }>({
    queryKey: ["bot-info"],
    queryFn: () => api("/api/admin/bot-info"),
    staleTime: Infinity,
  });

  const sysSettings = useQuery<{ referral_default_percent: number }>({
    queryKey: ["settings", "system"],
    queryFn: () => api("/api/admin/settings/system"),
    staleTime: 60000,
  });
  const defaultPercent = sysSettings.data?.referral_default_percent ?? 10;

  const toggleBlock = useMutation({
    mutationFn: () =>
      api(`/api/admin/users/${userId}/${details.data?.user.is_blocked ? "unblock" : "block"}`, {
        method: "POST",
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["user-details", userId] }),
  });

  const grant = useMutation({
    mutationFn: (body: {
      access_type: string;
      server_id?: number;
      duration_days?: number;
    }) =>
      api(`/api/admin/users/${userId}/grant`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["user-details", userId] }),
  });

  const extend = useMutation({
    mutationFn: ({ accessId, days }: { accessId: number; days: number }) =>
      api(`/api/admin/accesses/${accessId}/extend`, {
        method: "POST",
        body: JSON.stringify({ days }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["user-details", userId] }),
  });

  const deactivate = useMutation({
    mutationFn: (accessId: number) =>
      api(`/api/admin/accesses/${accessId}/deactivate`, { method: "POST" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["user-details", userId] }),
  });

  const removeAccess = useMutation({
    mutationFn: (accessId: number) =>
      api(`/api/admin/accesses/${accessId}`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["user-details", userId] }),
  });

  const rotate = useMutation({
    mutationFn: (body: { accessId: number; server_id: number; extra_days: number }) =>
      api(`/api/admin/accesses/${body.accessId}/rotate`, {
        method: "POST",
        body: JSON.stringify({ server_id: body.server_id, extra_days: body.extra_days }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["user-details", userId] }),
  });

  const [rotateFor, setRotateFor] = useState<number | null>(null);

  if (details.isLoading) return <div className="p-4 md:p-8 text-zinc-400">Загрузка…</div>;
  if (details.isError || !details.data)
    return <div className="p-4 md:p-8 text-red-400">Не нашли юзера</div>;

  const d = details.data;
  const u = d.user;

  const now = Date.now();
  const activeTest = d.accesses.find(
    (a) => a.is_active && a.access_type === "test" && new Date(a.expires_at).getTime() > now,
  );
  const activePaid = d.accesses.find(
    (a) => a.is_active && a.access_type === "paid" && new Date(a.expires_at).getTime() > now,
  );
  // Новая модель: активные proxy-подписки (Subscription на хосте).
  const activeSubs = (d.proxy_subs ?? []).filter((s) => s.is_active);
  const subExpires = activeSubs
    .map((s) => (s.expires_at ? new Date(s.expires_at).getTime() : 0))
    .sort((a, b) => b - a)[0];

  return (
    <div className="p-4 md:p-8">
      <PageHeader
        title={`Юзер #${u.id}`}
        right={
          <Link to="/users" className="text-sm text-zinc-400 hover:text-zinc-200">
            ← к списку
          </Link>
        }
      />

      {/* HERO — профиль клиента */}
      <div className="rounded-2xl card-elev p-5 mb-4">
        <div className="flex items-start gap-4 flex-wrap">
          <div className="w-14 h-14 rounded-2xl bg-brand-grad shadow-glow flex items-center justify-center text-brand-fg font-black text-xl shrink-0">
            {(u.first_name || u.username || "U").slice(0, 1).toUpperCase()}
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2.5 flex-wrap">
              <span className="text-xl font-bold text-zinc-50 truncate">
                {u.first_name || (u.username ? `@${u.username}` : `#${u.id}`)}
              </span>
              {u.username && (
                <a
                  href={`https://t.me/${u.username}`}
                  target="_blank"
                  rel="noreferrer"
                  className="text-brand hover:underline text-sm"
                >
                  @{u.username}
                </a>
              )}
            </div>
            <div className="text-xs text-zinc-500 font-mono mt-1">
              tg {u.telegram_id} · создан {dt(u.created_at)}
            </div>
            <div className="flex items-center gap-2 flex-wrap mt-3">
              {u.is_blocked && <Pill tone="danger">заблокирован</Pill>}
              {activePaid || activeSubs.some((s) => s.plan === "paid") ? (
                <Pill tone="success">
                  Proxy · до {dt(activePaid ? activePaid.expires_at : new Date(subExpires).toISOString())}
                </Pill>
              ) : activeTest || activeSubs.some((s) => s.plan === "trial") ? (
                <Pill tone="warning">Триал · proxy</Pill>
              ) : (
                <Pill>без proxy</Pill>
              )}
              {vpn.data?.exists ? (
                vpn.data.active ? (
                  <Pill tone="success">VPN · активен</Pill>
                ) : (
                  <Pill tone="danger">VPN · выкл</Pill>
                )
              ) : vpn.data?.available === false ? null : (
                <Pill>без VPN</Pill>
              )}
              <Pill tone="neutral">баланс {d.balance} ₽</Pill>
            </div>
          </div>
          <div className="flex gap-2 flex-wrap">
            {u.username && (
              <Button
                variant="tg"
                onClick={() => window.open(`https://t.me/${u.username}`, "_blank", "noreferrer")}
              >
                <TelegramIcon />
                Telegram
              </Button>
            )}
            <Button
              variant={u.is_blocked ? "success" : "danger"}
              onClick={() => toggleBlock.mutate()}
              disabled={toggleBlock.isPending}
            >
              {u.is_blocked ? "Разблокировать" : "Заблокировать"}
            </Button>
          </div>
        </div>
      </div>

      <section className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6 items-stretch">
        <GrantCard
          userId={userId}
          legacyServers={(servers.data?.servers ?? []).filter(
            (s) => s.is_active && s.server_type !== "ad",
          )}
          onLegacyGrant={(body) => grant.mutate(body)}
          legacyPending={grant.isPending}
          legacyError={grant.isError ? (grant.error as Error).message : null}
          hasActiveTest={!!activeTest}
          onDone={() => qc.invalidateQueries({ queryKey: ["user-details", userId] })}
        />

        <Card title="VPN">
          {vpn.isLoading ? (
            <div className="text-zinc-500 text-sm">Загрузка…</div>
          ) : !vpn.data?.available ? (
            <div className="text-zinc-600 text-sm leading-relaxed">
              Remnawave недоступен — на дев-стенде VPN-панель изолирована.
            </div>
          ) : !vpn.data.exists ? (
            <div className="text-zinc-500 text-sm">VPN не выдавался.</div>
          ) : (
            <div className="space-y-2.5">
              <div className="grid grid-cols-2 gap-2">
                <div className="rounded-lg bg-white/[0.02] border border-white/[0.05] px-2.5 py-2">
                  <div className="text-[10px] uppercase tracking-wide text-zinc-600 mb-1">статус</div>
                  {vpn.data.active ? (
                    <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-emerald-400">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 shadow-[0_0_6px_1px_rgba(52,211,153,.5)]" />
                      активен
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-rose-400">
                      <span className="w-1.5 h-1.5 rounded-full bg-rose-400" />
                      {vpn.data.status ?? "выкл"}
                    </span>
                  )}
                </div>
                <div className="rounded-lg bg-white/[0.02] border border-white/[0.05] px-2.5 py-2">
                  <div className="text-[10px] uppercase tracking-wide text-zinc-600 mb-1">устройства</div>
                  <div className="text-lg font-bold tabular-nums text-zinc-100 font-mono leading-none">
                    {vpn.data.devices}
                    <span className="text-sm text-zinc-500">/{vpn.data.device_limit}</span>
                  </div>
                </div>
              </div>
              <div className="rounded-lg bg-white/[0.02] border border-white/[0.05] px-2.5 py-2 flex items-center justify-between">
                <span className="text-[10px] uppercase tracking-wide text-zinc-600">истекает</span>
                <span className="text-sm text-zinc-200 tabular-nums">
                  {vpn.data.expire_at ? dt(vpn.data.expire_at) : "—"}
                </span>
              </div>

              {/* Управление доступом прямо здесь — чтобы не ходить в Remnawave. */}
              <VpnPowerButton
                userId={u.id}
                active={!!vpn.data.active}
                onDone={() => qc.invalidateQueries({ queryKey: ["user-vpn", userId] })}
              />

              {(vpn.data.sub_url || vpn.data.panel_url) && (
                <div className="pt-1 space-y-2">
                  <div className="text-[11px] uppercase tracking-wider text-zinc-500">подписка</div>
                  {vpn.data.panel_url && (
                    <a
                      href={`${vpn.data.panel_url}/dashboard/management/users`}
                      target="_blank"
                      rel="noreferrer"
                      className="flex items-center justify-center gap-2 h-9 rounded-lg bg-brand/15 text-brand border border-brand/30 text-sm font-medium hover:bg-brand-grad hover:text-brand-fg hover:border-transparent transition-all"
                      title={`открыть панель Remnawave (найди юзера tg${u.telegram_id})`}
                    >
                      🔗 Открыть в Remnawave
                    </a>
                  )}
                  {vpn.data.sub_url && (
                    <a
                      href={vpn.data.sub_url}
                      target="_blank"
                      rel="noreferrer"
                      className="flex items-center justify-center gap-2 h-9 w-full rounded-lg bg-white/[0.04] text-zinc-300 border border-white/[0.08] text-sm hover:bg-white/[0.08] hover:text-white transition-all"
                      title="открыть страницу подписки юзера"
                    >
                      🔗 Открыть ссылку
                    </a>
                  )}
                </div>
              )}
            </div>
          )}
        </Card>

        <Card title="Реферальная система">
          {/* воронка */}
          <div className="grid grid-cols-3 gap-2 mb-3">
            <MiniKpi label="привёл" value={d.referrals.length} />
            <MiniKpi label="баланс, ₽" value={d.balance} accent />
            <MiniKpi
              label="%"
              value={d.referral_percent ?? defaultPercent}
              suffix={d.referral_percent == null ? "деф" : ""}
            />
          </div>

          {botInfo.data?.username && (
            <div className="mb-3">
              <div className="text-xs text-zinc-500 mb-1">реф-ссылка юзера</div>
              <button
                onClick={() =>
                  navigator.clipboard?.writeText(
                    `https://telegram.me/${botInfo.data!.username}?start=ref${u.telegram_id}`,
                  )
                }
                className="w-full text-left font-mono text-[11px] text-zinc-400 bg-black/25 border border-white/[0.06] rounded-lg px-3 py-2 truncate hover:border-brand/40 transition-colors"
                title="скопировать"
              >
                telegram.me/{botInfo.data.username}?start=ref{u.telegram_id}
              </button>
            </div>
          )}

          <dl className="text-sm space-y-2.5">
            <Row
              k="баланс, ₽"
              v={
                <BalanceEdit
                  userId={userId}
                  value={d.balance}
                  onSaved={() =>
                    qc.invalidateQueries({ queryKey: ["user-details", userId] })
                  }
                />
              }
            />
            <Row
              k="персональный %"
              v={
                <RefPercentEdit
                  userId={userId}
                  value={d.referral_percent}
                  placeholder={String(defaultPercent)}
                  onSaved={() =>
                    qc.invalidateQueries({ queryKey: ["user-details", userId] })
                  }
                />
              }
            />
            <Row
              k="кем приведён"
              v={
                d.referrer ? (
                  <Link
                    to={`/users/${d.referrer.id}`}
                    className="text-zinc-100 hover:text-brand hover:underline"
                  >
                    {d.referrer.username ? `@${d.referrer.username}` : `#${d.referrer.id}`}
                  </Link>
                ) : (
                  <span className="text-zinc-600">—</span>
                )
              }
            />
          </dl>
          <Link to="/referrals" className="inline-block mt-3 text-xs text-brand hover:underline">
            вся реферальная статистика →
          </Link>
        </Card>
      </section>

      <section className="mb-6">
        <h2 className="text-sm uppercase tracking-wider text-zinc-400 mb-2">История баланса</h2>
        <Card>
          <BalanceLog userId={userId} />
        </Card>
      </section>

      {activeSubs.length > 0 && (
        <section className="mb-6">
          <h2 className="text-sm uppercase tracking-wider text-zinc-400 mb-2">Proxy-подписки (новая модель)</h2>
          <Table>
            <thead>
              <tr>
                <Th>id</Th>
                <Th>план</Th>
                <Th>хост</Th>
                <Th>устройств</Th>
                <Th>до</Th>
                <Th>ссылка</Th>
              </tr>
            </thead>
            <tbody>
              {activeSubs.map((s) => (
                <tr key={s.id}>
                  <Td className="text-zinc-500">{s.id}</Td>
                  <Td><Pill tone={s.plan === "paid" ? "success" : "neutral"}>{s.plan}</Pill></Td>
                  <Td>{s.endpoint_name ?? `#${s.endpoint_id}`}</Td>
                  <Td className="tabular-nums">{s.device_limit}</Td>
                  <Td>{s.expires_at ? dt(s.expires_at) : "—"}</Td>
                  <Td>
                    {s.link ? (
                      <button
                        onClick={() => navigator.clipboard?.writeText(s.link)}
                        className="text-brand hover:underline text-xs"
                        title={s.link}
                      >
                        копировать
                      </button>
                    ) : "—"}
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
        </section>
      )}

      <section className="mb-6">
        <h2 className="text-sm uppercase tracking-wider text-zinc-400 mb-2">Доступы (старая модель)</h2>
        {d.accesses.length === 0 ? (
          <div className="text-zinc-500 text-sm">Нет доступов на старых серверах.</div>
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>id</Th>
                <Th>тип</Th>
                <Th>сервер</Th>
                <Th>до</Th>
                <Th>статус</Th>
                <Th align="right">действия</Th>
              </tr>
            </thead>
            <tbody>
              {d.accesses.map((a) => (
                <tr key={a.id}>
                  <Td className="text-zinc-500">{a.id}</Td>
                  <Td>
                    <Pill tone={a.access_type === "paid" ? "success" : "neutral"}>
                      {a.access_type}
                    </Pill>
                  </Td>
                  <Td>{a.server ? a.server.name : `#${a.server_id}`}</Td>
                  <Td>{dt(a.expires_at)}</Td>
                  <Td>
                    {a.is_active ? (
                      <Pill tone="success">активен</Pill>
                    ) : (
                      <Pill>неактивен</Pill>
                    )}
                  </Td>
                  <Td align="right" fit>
                    <AccessActionsMenu
                      access={a}
                      onExtend={() =>
                        extend.mutate({
                          accessId: a.id,
                          days: 30,
                        })
                      }
                      onRotate={() => setRotateFor(a.id)}
                      onDeactivate={() => deactivate.mutate(a.id)}
                      onDelete={async () => {
                        if (await confirmPopup({ title: "Удалить доступ?", body: `#${a.id} — это нельзя отменить.`, danger: true, confirmLabel: "Удалить" })) {
                          removeAccess.mutate(a.id);
                        }
                      }}
                      busy={
                        extend.isPending ||
                        rotate.isPending ||
                        deactivate.isPending ||
                        removeAccess.isPending
                      }
                    />
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </section>

      <section className="mb-6">
        <h2 className="text-sm uppercase tracking-wider text-zinc-400 mb-2">Платежи</h2>
        {d.payments.length === 0 ? (
          <div className="text-zinc-500 text-sm">Платежей нет.</div>
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>id</Th>
                <Th>сумма</Th>
                <Th>статус</Th>
                <Th>метод</Th>
                <Th>tx id</Th>
                <Th>когда</Th>
              </tr>
            </thead>
            <tbody>
              {d.payments.map((p) => (
                <tr key={p.id}>
                  <Td className="text-zinc-500">{p.id}</Td>
                  <Td>{formatPaymentAmount(p.amount, p.currency)}</Td>
                  <Td>
                    <Pill tone={p.status === "completed" ? "success" : "warning"}>{p.status}</Pill>
                  </Td>
                  <Td>{p.payment_method ?? "—"}</Td>
                  <Td className="font-mono text-xs text-zinc-400">
                    {p.payment_id ? `${p.payment_id.slice(0, 10)}…` : "—"}
                  </Td>
                  <Td className="text-zinc-500">{dt(p.created_at)}</Td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </section>

      <RotateAccessModal
        accessId={rotateFor}
        currentAccess={
          rotateFor != null ? d.accesses.find((a) => a.id === rotateFor) ?? null : null
        }
        servers={(servers.data?.servers ?? []).filter(
          (s) => s.is_active && s.server_type !== "ad",
        )}
        onClose={() => setRotateFor(null)}
        onSubmit={(server_id, extra_days) => {
          if (rotateFor != null) {
            rotate.mutate(
              { accessId: rotateFor, server_id, extra_days },
              { onSettled: () => setRotateFor(null) },
            );
          }
        }}
        pending={rotate.isPending}
        error={rotate.isError ? (rotate.error as Error).message : null}
      />

      {d.referrals.length > 0 && (
        <section>
          <h2 className="text-sm uppercase tracking-wider text-zinc-400 mb-2">
            Приведённые ({d.referrals.length})
          </h2>
          <Table>
            <thead>
              <tr>
                <Th>id</Th>
                <Th>telegram_id</Th>
                <Th>username</Th>
                <Th>пришёл</Th>
              </tr>
            </thead>
            <tbody>
              {d.referrals.map((r) => (
                <tr key={r.id}>
                  <Td>
                    <Link className="text-brand hover:underline" to={`/users/${r.id}`}>
                      #{r.id}
                    </Link>
                  </Td>
                  <Td className="font-mono">{r.telegram_id}</Td>
                  <Td>
                    {r.username ? (
                      <Link
                        to={`/users/${r.id}`}
                        className="text-zinc-100 hover:text-brand hover:underline"
                      >
                        @{r.username}
                      </Link>
                    ) : (
                      <span className="text-zinc-600">—</span>
                    )}
                  </Td>
                  <Td className="text-zinc-500">{dt(r.created_at)}</Td>
                </tr>
              ))}
            </tbody>
          </Table>
        </section>
      )}
    </div>
  );
}

interface EndpointLite {
  id: number;
  name: string;
  address: string;
  kind: string;
  transport: string;
  is_active: boolean;
}

function GrantCard({
  userId,
  legacyServers,
  onLegacyGrant,
  legacyPending,
  legacyError,
  hasActiveTest,
  onDone,
}: {
  userId: number;
  legacyServers: Server[];
  onLegacyGrant: (body: { access_type: string; server_id?: number; duration_days?: number }) => void;
  legacyPending: boolean;
  legacyError: string | null;
  hasActiveTest: boolean;
  onDone: () => void;
}) {
  const [tab, setTab] = useState<"proxy" | "vpn" | "legacy">("proxy");
  const [days, setDays] = useState("30");
  const [devices, setDevices] = useState("3");
  const [endpointId, setEndpointId] = useState("");
  const [legacyServer, setLegacyServer] = useState("");

  const eps = useQuery<{ endpoints: EndpointLite[] }>({
    queryKey: ["endpoints"],
    queryFn: () => api("/api/admin/endpoints"),
    enabled: tab === "proxy",
  });
  const hosts = (eps.data?.endpoints ?? []).filter((e) => e.is_active && e.kind !== "ad");

  const grantProxy = useMutation({
    mutationFn: () =>
      api<{ ok: boolean; link?: string; provision?: { ok: boolean } }>(
        `/api/admin/users/${userId}/grant-proxy`,
        {
          method: "POST",
          body: JSON.stringify({
            endpoint_id: Number(endpointId),
            duration_days: Number(days) || 30,
            device_limit: Number(devices) || 3,
          }),
        },
      ),
    onSuccess: (r) => {
      popup({
        tone: r.ok ? "success" : "warning",
        title: r.ok ? "Proxy выдан" : "Подписка создана",
        body:
          (r.provision?.ok ? "Секрет залит на exit. Юзеру ушло уведомление с ссылкой." : "Провижн не прошёл — разверни каскад хоста.") +
          (r.link ? `\n\n${r.link}` : ""),
      });
      onDone();
    },
    onError: (e) => popup({ tone: "error", title: "Proxy", body: (e as Error).message }),
  });

  const grantVpn = useMutation({
    mutationFn: () =>
      api<{ ok: boolean; error?: string }>(`/api/admin/users/${userId}/grant-vpn`, {
        method: "POST",
        body: JSON.stringify({ duration_days: Number(days) || 30, device_limit: Number(devices) || 3 }),
      }),
    onSuccess: (r) => {
      popup({ tone: r.ok ? "success" : "error", title: r.ok ? "VPN выдан" : "VPN", body: r.ok ? "Готово." : (r.error ?? "не удалось") });
      onDone();
    },
    onError: (e) => popup({ tone: "error", title: "VPN", body: (e as Error).message }),
  });

  const tabs: [typeof tab, string][] = [
    ["proxy", "Proxy"],
    ["vpn", "VPN"],
    ["legacy", "Сервер"],
  ];

  return (
    <Card title="Выдать доступ">
      <div className="inline-flex items-center gap-1 mb-3 p-1 rounded-lg bg-white/[0.03] border border-white/[0.06]">
        {tabs.map(([k, label]) => (
          <button
            key={k}
            onClick={() => setTab(k)}
            className={`h-7 px-3 rounded-md text-xs font-medium transition-all ${
              tab === k ? "bg-brand-soft text-brand glow-ring" : "text-zinc-400 hover:text-zinc-200"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {tab !== "legacy" && (
        <div className="flex items-center gap-2 mb-3">
          <label className="text-xs text-zinc-400 shrink-0">дней:</label>
          <Input type="number" min={1} max={3650} value={days} onChange={(e) => setDays(e.target.value)} className="h-9 w-20" />
          <div className="flex gap-1">
            {[7, 30, 90, 365].map((d) => (
              <button
                key={d}
                type="button"
                onClick={() => setDays(String(d))}
                className={`h-9 px-2.5 text-xs rounded-lg border transition-all ${
                  days === String(d)
                    ? "bg-brand-soft text-brand border-transparent glow-ring"
                    : "border-white/[0.08] text-zinc-400 hover:text-zinc-200 hover:bg-white/[0.04]"
                }`}
              >
                {d}
              </button>
            ))}
          </div>
        </div>
      )}

      {tab === "proxy" && (
        <div className="space-y-3">
          <div>
            <label className="block text-xs text-zinc-500 mb-1">Хост (домен, что видит юзер)</label>
            <DarkSelect
              value={endpointId}
              onChange={setEndpointId}
              placeholder="— выбрать хост —"
              options={hosts.map((h) => ({
                value: String(h.id),
                label: h.name,
                hint: h.address,
              }))}
            />
            {hosts.length === 0 && !eps.isLoading && (
              <p className="text-xs text-amber-400/80 mt-1">Нет активных хостов — создай в разделе «Хосты».</p>
            )}
          </div>
          <div className="flex items-center gap-2">
            <label className="text-xs text-zinc-400 shrink-0">устройств:</label>
            <Input type="number" min={1} max={50} value={devices} onChange={(e) => setDevices(e.target.value)} className="h-9 w-20" />
          </div>
          <Button
            variant="primary"
            disabled={grantProxy.isPending || !endpointId}
            onClick={() => grantProxy.mutate()}
          >
            {grantProxy.isPending ? "Выдаю + провижн…" : `Выдать proxy +${Number(days) || 0} дн.`}
          </Button>
          <p className="text-xs text-zinc-600 leading-relaxed">
            Создаёт подписку на хост и заливает секрет на serving-exit ноды по SSH.
            Если каскад ещё не развёрнут — сделай «Развернуть каскад» в карточке хоста.
          </p>
        </div>
      )}

      {tab === "vpn" && (
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <label className="text-xs text-zinc-400 shrink-0">устройств:</label>
            <Input type="number" min={1} max={50} value={devices} onChange={(e) => setDevices(e.target.value)} className="h-9 w-20" />
          </div>
          <Button variant="primary" disabled={grantVpn.isPending} onClick={() => grantVpn.mutate()}>
            {grantVpn.isPending ? "Выдаю…" : `Выдать VPN +${Number(days) || 0} дн.`}
          </Button>
          <p className="text-xs text-zinc-600 leading-relaxed">
            Выдаёт или продлевает VPN на выбранный срок и присылает юзеру ссылку
            прямо в бота. Включение и отключение доступа — в карточке VPN справа.
          </p>
        </div>
      )}

      {tab === "legacy" && (
        <div className="space-y-3">
          <div>
            <label className="block text-xs text-zinc-500 mb-1">Сервер (старая модель)</label>
            <DarkSelect
              value={legacyServer}
              onChange={setLegacyServer}
              placeholder="Авто — наименее загруженный"
              options={[
                { value: "", label: "Авто — наименее загруженный" },
                ...legacyServers.map((s) => ({
                  value: String(s.id),
                  label: s.name,
                  hint: s.server_type,
                })),
              ]}
            />
          </div>
          <div className="flex items-center gap-2">
            <label className="text-xs text-zinc-400 shrink-0">дней:</label>
            <Input type="number" min={1} max={3650} value={days} onChange={(e) => setDays(e.target.value)} className="h-9 w-24" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Button
              disabled={legacyPending || hasActiveTest}
              onClick={() =>
                onLegacyGrant({ access_type: "test", ...(legacyServer ? { server_id: Number(legacyServer) } : {}) })
              }
            >
              test 48ч
            </Button>
            <Button
              disabled={legacyPending || (Number(days) || 0) <= 0}
              onClick={() =>
                onLegacyGrant({
                  access_type: "paid",
                  duration_days: Number(days) || 30,
                  ...(legacyServer ? { server_id: Number(legacyServer) } : {}),
                })
              }
            >
              paid +{Number(days) || 0} дн.
            </Button>
          </div>
          {legacyError && <div className="text-xs text-red-400">{legacyError}</div>}
        </div>
      )}
    </Card>
  );
}

function MiniKpi({
  label,
  value,
  accent = false,
  suffix,
}: {
  label: string;
  value: number;
  accent?: boolean;
  suffix?: string;
}) {
  return (
    <div className="rounded-lg bg-white/[0.02] border border-white/[0.05] px-2.5 py-2">
      <div className="text-[10px] uppercase tracking-wide text-zinc-600">{label}</div>
      <div className={`text-lg font-bold tabular-nums ${accent ? "text-brand" : "text-zinc-100"}`}>
        {value}
        {suffix ? <span className="text-xs text-zinc-500 ml-0.5">{suffix}</span> : null}
      </div>
    </div>
  );
}

function Row({ k, v }: { k: string; v: React.ReactNode }) {
  return (
    <div className="flex justify-between gap-6">
      <dt className="text-zinc-500 shrink-0">{k}</dt>
      <dd className="text-zinc-100 text-right">{v}</dd>
    </div>
  );
}

interface BalanceTx {
  id: number;
  amount: number;
  balance_after: number | null;
  kind: string;
  note: string | null;
  created_at: string;
}

const BAL_KIND: Record<string, string> = {
  topup_card: "💳 Пополнение картой",
  topup_admin: "👤 Пополнение админом",
  admin_add: "👤 Правка админа (+)",
  admin_set: "👤 Установка админом",
  referral: "🎁 Реферальный бонус",
  traffer: "📊 Выплата трафферу",
  purchase_proxy: "🚀 Покупка прокси",
  purchase_vpn: "🛡 Покупка VPN",
  purchase_devices: "➕ Докупка устройств",
  rollback: "↩️ Возврат (грант не прошёл)",
  adjust: "✏️ Корректировка",
};

function VpnPowerButton({
  userId,
  active,
  onDone,
}: {
  userId: number;
  active: boolean;
  onDone: () => void;
}) {
  const act = useMutation({
    mutationFn: () =>
      api<{ ok: boolean; error?: string }>(
        `/api/admin/users/${userId}/${active ? "revoke-vpn" : "enable-vpn"}`,
        { method: "POST" },
      ),
    onSuccess: (r) => {
      popup({
        tone: r.ok ? "success" : "error",
        title: r.ok ? (active ? "VPN отключён" : "VPN включён") : "VPN",
        body: r.ok
          ? active
            ? "Доступ отключён. Срок подписки сохранён."
            : "Доступ снова активен."
          : (r.error ?? "не удалось"),
      });
      onDone();
    },
    onError: (e) => popup({ tone: "error", title: "VPN", body: (e as Error).message }),
  });

  return (
    <button
      disabled={act.isPending}
      onClick={() => act.mutate()}
      className={`w-full h-9 rounded-lg text-sm font-medium border transition-all disabled:opacity-60 ${
        active
          ? "bg-rose-500/10 text-rose-300 border-rose-500/25 hover:bg-rose-500/20"
          : "bg-emerald-500/10 text-emerald-300 border-emerald-500/25 hover:bg-emerald-500/20"
      }`}
    >
      {act.isPending ? "…" : active ? "Отключить VPN" : "Включить VPN"}
    </button>
  );
}

function BalanceLog({ userId }: { userId: number }) {
  const q = useQuery<{ items: BalanceTx[] }>({
    queryKey: ["balance-log", userId],
    queryFn: () => api(`/api/admin/users/${userId}/balance-log?limit=100`),
  });
  const items = q.data?.items ?? [];
  if (q.isLoading) return <div className="text-xs text-zinc-500">Загрузка…</div>;
  if (items.length === 0)
    return <div className="text-xs text-zinc-600">Пока нет операций по балансу.</div>;
  return (
    <div className="overflow-x-auto -mx-1">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-[11px] uppercase tracking-wider text-zinc-500 text-left">
            <th className="px-2 py-1.5 font-medium">Когда</th>
            <th className="px-2 py-1.5 font-medium">Операция</th>
            <th className="px-2 py-1.5 font-medium text-right">Сумма</th>
            <th className="px-2 py-1.5 font-medium text-right">Баланс после</th>
          </tr>
        </thead>
        <tbody>
          {items.map((t) => {
            const pos = t.amount >= 0;
            return (
              <tr key={t.id} className="border-t border-white/[0.05]">
                <td className="px-2 py-1.5 text-zinc-400 whitespace-nowrap">
                  {new Date(t.created_at).toLocaleString("ru-RU", { dateStyle: "short", timeStyle: "short" })}
                </td>
                <td className="px-2 py-1.5 text-zinc-200">
                  {BAL_KIND[t.kind] ?? t.kind}
                  {t.note ? <span className="text-zinc-500 text-xs"> · {t.note}</span> : null}
                </td>
                <td className={`px-2 py-1.5 text-right font-mono tabular-nums ${pos ? "text-emerald-400" : "text-rose-400"}`}>
                  {pos ? "+" : ""}{Number(t.amount).toFixed(0)} ₽
                </td>
                <td className="px-2 py-1.5 text-right font-mono tabular-nums text-zinc-400">
                  {t.balance_after != null ? `${Number(t.balance_after).toFixed(0)} ₽` : "—"}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function BalanceEdit({
  userId,
  value,
  onSaved,
}: {
  userId: number;
  value: number;
  onSaved: () => void;
}) {
  const [input, setInput] = useState<string>(String(value));
  useEffect(() => setInput(String(value)), [value]);
  const trimmed = input.trim();
  const parsed = Number(trimmed);
  const invalid = trimmed === "" || Number.isNaN(parsed);

  const save = useMutation({
    mutationFn: (op: "set" | "add") =>
      api<{ ok: boolean; balance: number }>(`/api/admin/users/${userId}/balance`, {
        method: "PUT",
        body: JSON.stringify({ op, amount: parsed }),
      }),
    onSuccess: () => onSaved(),
  });

  return (
    <div className="inline-flex items-center gap-2">
      <input
        type="text"
        inputMode="decimal"
        value={input}
        onChange={(e) => setInput(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" && !invalid) save.mutate("set");
          if (e.key === "Escape") setInput(String(value));
        }}
        className="h-7 w-24 px-2 rounded-md bg-zinc-950 border border-zinc-800 text-sm text-zinc-100 text-right focus:outline-none focus:border-brand"
      />
      <span className="text-zinc-500 text-xs">₽</span>
      <button
        onClick={() => save.mutate("set")}
        disabled={save.isPending || invalid}
        title="установить точное значение"
        className="h-7 px-3 text-xs rounded-md bg-brand/15 text-brand border border-brand/40 hover:bg-brand hover:text-brand-fg hover:border-brand disabled:opacity-40"
      >
        {save.isPending ? "…" : "Установить"}
      </button>
      <button
        onClick={() => save.mutate("add")}
        disabled={save.isPending || invalid}
        title="прибавить введённое к текущему (минус = списать)"
        className="h-7 px-2 text-xs rounded-md bg-zinc-900 text-zinc-300 border border-zinc-800 hover:bg-zinc-700 hover:text-white disabled:opacity-40"
      >
        +добавить
      </button>
      {invalid && <span className="text-xs text-red-400">число</span>}
      {save.isError && (
        <span className="text-xs text-red-400">{(save.error as Error).message}</span>
      )}
    </div>
  );
}

function RefPercentEdit({
  userId,
  value,
  placeholder,
  onSaved,
}: {
  userId: number;
  value: number | null;
  placeholder: string;
  onSaved: () => void;
}) {
  const initial = value != null ? String(value) : "";
  const [input, setInput] = useState<string>(initial);

  useEffect(() => {
    setInput(value != null ? String(value) : "");
  }, [value]);

  const trimmed = input.trim();
  const parsed = trimmed === "" ? null : Number(trimmed);
  const invalid =
    parsed != null && (Number.isNaN(parsed) || parsed < 0 || parsed > 100);
  const dirty = trimmed !== initial;

  const save = useMutation({
    mutationFn: () =>
      api(`/api/admin/users/${userId}/referral-percent`, {
        method: "PATCH",
        body: JSON.stringify({ percent: parsed }),
      }),
    onSuccess: () => onSaved(),
  });

  return (
    <div className="inline-flex items-center gap-2">
      <input
        type="text"
        inputMode="decimal"
        value={input}
        onChange={(e) => setInput(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" && dirty && !invalid) save.mutate();
          if (e.key === "Escape") setInput(initial);
        }}
        placeholder={placeholder}
        title={`не задан — применяется дефолт ${placeholder}%`}
        className="h-7 w-20 px-2 rounded-md bg-zinc-950 border border-zinc-800 text-sm text-zinc-100 text-right focus:outline-none focus:border-brand placeholder:text-zinc-600"
      />
      <span className="text-zinc-500 text-xs">%</span>
      {dirty && (
        <button
          onClick={() => save.mutate()}
          disabled={save.isPending || invalid}
          className="h-7 px-3 text-xs rounded-md bg-brand/15 text-brand border border-brand/40 hover:bg-brand hover:text-brand-fg hover:border-brand disabled:opacity-40"
        >
          {save.isPending ? "…" : "Сохранить"}
        </button>
      )}
      {dirty && (
        <button
          onClick={() => setInput(initial)}
          disabled={save.isPending}
          className="h-7 px-2 text-xs rounded-md bg-zinc-900 text-zinc-400 border border-zinc-800 hover:bg-zinc-700 hover:text-white disabled:opacity-40"
        >
          ✕
        </button>
      )}
      {invalid && (
        <span className="text-xs text-red-400">0…100</span>
      )}
      {save.isError && (
        <span className="text-xs text-red-400">{(save.error as Error).message}</span>
      )}
    </div>
  );
}

function AccessActionsMenu({
  access,
  onExtend,
  onRotate,
  onDeactivate,
  onDelete,
  busy,
}: {
  access: Access;
  onExtend: () => void;
  onRotate: () => void;
  onDeactivate: () => void;
  onDelete: () => void;
  busy: boolean;
}) {
  const [open, setOpen] = useState(false);
  const btnRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; right: number } | null>(null);

  useLayoutEffect(() => {
    if (!open || !btnRef.current) return;
    const r = btnRef.current.getBoundingClientRect();
    setPos({
      top: r.bottom + 4,
      right: window.innerWidth - r.right,
    });
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      const t = e.target as Node;
      if (
        btnRef.current?.contains(t) ||
        menuRef.current?.contains(t)
      ) {
        return;
      }
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    const onScrollResize = () => setOpen(false);
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    window.addEventListener("scroll", onScrollResize, true);
    window.addEventListener("resize", onScrollResize);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("scroll", onScrollResize, true);
      window.removeEventListener("resize", onScrollResize);
    };
  }, [open]);

  const fire = (fn: () => void) => {
    setOpen(false);
    fn();
  };

  return (
    <>
      <button
        ref={btnRef}
        onClick={() => setOpen((v) => !v)}
        disabled={busy}
        className="h-8 px-3 text-xs font-medium rounded-md border bg-zinc-900 text-zinc-300 border-zinc-800 hover:bg-zinc-700 hover:text-white hover:border-zinc-600 transition-colors disabled:opacity-40"
      >
        Действия ▾
      </button>
      {open &&
        pos &&
        createPortal(
          <div
            ref={menuRef}
            style={{ position: "fixed", top: pos.top, right: pos.right, zIndex: 50 }}
            className="min-w-[200px] rounded-lg border border-zinc-800 bg-zinc-950 shadow-xl py-1"
          >
            <MenuItem onClick={() => fire(onExtend)}>+30 дней</MenuItem>
            {access.is_active && access.access_type === "paid" && (
              <MenuItem onClick={() => fire(onRotate)}>Перенести на сервер…</MenuItem>
            )}
            {access.is_active && (
              <MenuItem onClick={() => fire(onDeactivate)} tone="danger">
                Деактивировать
              </MenuItem>
            )}
            <MenuItem onClick={() => fire(onDelete)} tone="danger">
              Удалить
            </MenuItem>
          </div>,
          document.body,
        )}
    </>
  );
}

function MenuItem({
  children,
  onClick,
  tone = "default",
}: {
  children: React.ReactNode;
  onClick: () => void;
  tone?: "default" | "danger";
}) {
  const cls =
    tone === "danger"
      ? "text-red-300 hover:bg-red-950/40"
      : "text-zinc-200 hover:bg-zinc-800/60";
  return (
    <button
      onClick={onClick}
      className={`w-full text-left px-3 py-2 text-sm transition-colors ${cls}`}
    >
      {children}
    </button>
  );
}

function RotateAccessModal({
  accessId,
  currentAccess,
  servers,
  onClose,
  onSubmit,
  pending,
  error,
}: {
  accessId: number | null;
  currentAccess: Access | null;
  servers: Server[];
  onClose: () => void;
  onSubmit: (server_id: number, extra_days: number) => void;
  pending: boolean;
  error: string | null;
}) {
  const [serverId, setServerId] = useState<string>("");
  const [extraDays, setExtraDays] = useState<string>("0");

  useEffect(() => {
    if (accessId != null) {
      // default to first server that's not the current
      const candidates = servers.filter(
        (s) => !currentAccess || s.id !== currentAccess.server_id,
      );
      setServerId(candidates[0] ? String(candidates[0].id) : "");
      setExtraDays("0");
    }
  }, [accessId, currentAccess, servers]);

  const open = accessId != null;
  if (!open) return null;

  const days = Number(extraDays);
  const invalid =
    !serverId || Number.isNaN(days) || days < 0 || days > 3650;

  const remainingDays = currentAccess
    ? Math.max(
        0,
        Math.ceil(
          (new Date(currentAccess.expires_at).getTime() - Date.now()) /
            (1000 * 60 * 60 * 24),
        ),
      )
    : 0;
  const newTotalDays = remainingDays + days;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Перенести доступ #${accessId} на другой сервер`}
    >
      <div className="space-y-4">
        {currentAccess && (
          <div className="text-sm text-zinc-400">
            Сейчас: сервер{" "}
            <b className="text-zinc-100">
              {currentAccess.server?.name ?? `#${currentAccess.server_id}`}
            </b>
            , осталось <b className="text-zinc-100">{remainingDays}</b> дней.
          </div>
        )}
        <div>
          <div className="text-xs text-zinc-500 mb-1">Новый сервер</div>
          <DarkSelect
            value={serverId}
            onChange={setServerId}
            placeholder="— выбрать сервер —"
            options={servers
              .filter((s) => !currentAccess || s.id !== currentAccess.server_id)
              .map((s) => ({
                value: String(s.id),
                label: s.name,
                hint: s.server_type,
              }))}
          />
        </div>
        <div>
          <div className="text-xs text-zinc-500 mb-1">Добавить дней (опционально)</div>
          <Input
            value={extraDays}
            onChange={(e) => setExtraDays(e.target.value)}
            placeholder="0"
          />
          <p className="text-xs text-zinc-500 mt-1.5">
            0 = просто перенос без продления. {days > 0 && `Итого станет ${newTotalDays} дней.`}
          </p>
        </div>
        <div className="text-xs text-zinc-500">
          Будет: regenerate secret · UPDATE access.server_id · sync обоих
          серверов через SSH. Старая ссылка перестанет работать.
        </div>
        {error && <div className="text-xs text-red-400">{error}</div>}
        <div className="flex gap-3 pt-2 justify-end">
          <Button variant="ghost" onClick={onClose} disabled={pending}>
            Отмена
          </Button>
          <Button
            variant="primary"
            disabled={invalid || pending}
            onClick={() => onSubmit(Number(serverId), days)}
          >
            {pending ? "Переносим…" : days > 0 ? `Перенести и +${days}д` : "Перенести"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
